import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import {
  Calculator, X, Plus, Trash2, Save, CreditCard, Search
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import ProductoSearchSelect from "./ProductoSearchSelect";
import MoneyInput from "./ui/MoneyInput";
import { EntidadModal } from "../pages/CatastroPage";
import DateInput from "./ui/DateInput";

// Buscador de cliente con creación inline en el Catastro — mismo patrón que
// ClienteSearchSelect en Facturas (FacturaFormModal.jsx), adaptado a Presupuestos.
// Reemplaza el <select> nativo, que con muchas entidades cargadas no permitía
// buscar tipeando más de una letra (el typeahead nativo del navegador se
// reinicia entre pulsaciones si hay una pequeña pausa, dando la sensación de
// que el campo "se cuelga").
const ClienteSearchSelect = ({ empresas, value, onSelect, onCreated, token, logo }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [clienteModalNombre, setClienteModalNombre] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return (empresas || []).slice(0, 12);
    const q = query.toLowerCase();
    return (empresas || []).filter(e =>
      (e.nombre || "").toLowerCase().includes(q) ||
      (e.razon_social || "").toLowerCase().includes(q) ||
      (e.ruc || "").toLowerCase().includes(q)
    ).slice(0, 12);
  }, [empresas, query]);

  const exactMatch = (empresas || []).some(e => (e.nombre || e.razon_social || "").toLowerCase() === query.trim().toLowerCase());

  const handlePick = (emp) => {
    onSelect(emp);
    setQuery(""); setOpen(false); setClienteModalNombre("");
  };

  if (value?.id) {
    return (
      <div className="flex items-center gap-2 bg-arandu-dark border border-arandu-blue/30 rounded-md px-3 py-2.5 min-h-[46px]">
        <div className="flex-1 min-w-0">
          <span className="text-white text-sm font-medium truncate block">{value.nombre || value.razon_social}</span>
          {value.ruc && <span className="text-slate-500 text-xs">RUC: {value.ruc}</span>}
        </div>
        <button type="button" onClick={() => onSelect(null)} className="text-slate-500 hover:text-white shrink-0 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar entidad por nombre o RUC..."
          className="w-full bg-arandu-dark border border-white/10 text-white rounded-md pl-9 pr-3 py-3 text-sm focus:outline-none focus:border-arandu-blue placeholder:text-slate-600"
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-arandu-dark border border-white/10 rounded-lg shadow-xl overflow-hidden">
          {filtered.length > 0 && (
            <ul className="max-h-48 overflow-y-auto divide-y divide-white/5">
              {filtered.map(e => (
                <li key={e.id} onMouseDown={() => handlePick(e)}
                  className="px-3 py-2.5 hover:bg-white/5 cursor-pointer flex items-center justify-between">
                  <span className="text-white text-sm">{e.nombre || e.razon_social}</span>
                  {e.ruc && <span className="text-slate-500 text-xs ml-2 shrink-0">RUC: {e.ruc}</span>}
                </li>
              ))}
            </ul>
          )}
          {filtered.length === 0 && query.trim() && (
            <div className="px-3 py-2 text-slate-500 text-xs">Sin resultados para "{query.trim()}"</div>
          )}
          {query.trim() && !exactMatch && (
            <div className={filtered.length > 0 ? "border-t border-white/5" : ""}>
              <button type="button" onMouseDown={() => setClienteModalNombre(query.trim())}
                className="w-full px-3 py-2.5 text-left text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors">
                <Plus className="w-3.5 h-3.5 shrink-0" />
                Crear "<strong>{query.trim()}</strong>" como nueva entidad
              </button>
            </div>
          )}
        </div>
      )}
      {clienteModalNombre !== "" && (
        <EntidadModal
          entidad={{
            _presetRol: "es_cliente",
            _initialNombre: clienteModalNombre,
            _initialRazonSocial: clienteModalNombre,
          }}
          logo={logo}
          token={token}
          allowedRoles={{ es_cliente: true, es_proveedor: false, es_empleado: false }}
          onClose={() => setClienteModalNombre("")}
          onSaved={nuevo => {
            if (nuevo?.id) {
              onCreated?.(nuevo);
              handlePick(nuevo);
            } else {
              setClienteModalNombre("");
            }
          }}
        />
      )}
    </div>
  );
};

const PresupuestoFormModal = ({
  presupuesto,
  mode, // "edit", "copy", "new"
  onClose,
  onSaved,
  token,
  API,
  empresas,
  proveedores,
  productos,
  productosHabilitados = true,
  activeEmpresaPropia,
  isAdmin,
  hasPermission,
  defaultEmpresaId = "",
  canUseDescuento = false,
}) => {
  const canModoLibre = isAdmin || hasPermission("presupuestos.modo_libre");
  const defaultModo = productosHabilitados ? (canModoLibre ? "libre" : "productos") : "libre";
  const canModifyPrecioAbajo = isAdmin || hasPermission("presupuestos.modificar_precio_abajo");
  const canModifyPrecioArriba = isAdmin || hasPermission("presupuestos.modificar_precio_arriba");
  // Ver costo de compra: permiso granular propio de Presupuestos (presupuestos.ver_costo_compra),
  // independiente del permiso de Productos. Así se puede habilitar en un módulo sin habilitarlo en el otro.
  // En modo libre nunca se toca este permiso, porque ahí no se accede al catálogo.
  const puedeVerCostoCompra = isAdmin || hasPermission("presupuestos.ver_costo_compra");
  // Descuento global: permiso propio, independiente del descuento por ítem.
  const puedeDescuentoGlobal = isAdmin || hasPermission("presupuestos.descuentos_globales");

  // Helpers para precio de catálogo en ítems
  const isCatalogItem = (item) => !!item.producto_id && formData.modo === "productos";
  const isPrecioLocked = (item) => isCatalogItem(item) && !canModifyPrecioAbajo && !canModifyPrecioArriba;
  const handlePrecioDirectChange = (index, value) => {
    const item = formData.items[index];
    const catalogo = parseFloat(item.precio_catalogo ?? item.precio_unitario) || 0;
    const nuevo = parseFloat(value) || 0;
    if (!canModifyPrecioAbajo && nuevo < catalogo) return;
    if (!canModifyPrecioArriba && nuevo > catalogo) return;
    const costo = parseFloat(item.costo) || 0;
    const newMargen = costo > 0 ? ((nuevo / costo) - 1) * 100 : 0;
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], margen: newMargen };
    newItems[index] = calculateItem(newItems[index]);
    setFormData({ ...formData, items: newItems });
  };

  const getDefaultLogo = () => {
    if (!activeEmpresaPropia) return "arandujar";
    return activeEmpresaPropia.slug || "arandujar";
  };

  const getDefaultFormData = () => ({
    empresa_id: defaultEmpresaId || "",
    numero: "",
    nombre_archivo: "",
    logo_tipo: getDefaultLogo(),
    moneda: "PYG",
    forma_pago: "contado",
    condicion_pago: "contado",   // contado | credito_dias | credito_cuotas
    dias_credito: 30,
    num_cuotas_ref: 3,
    tasa_interes_ref: 0,
    fecha: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10),
    validez_dias: 15,
    tipo_cambio: "",
    modo: defaultModo,
    items: [{
      descripcion: "", observacion: "", observacion_oculta: "",
      cantidad: 1, costo: 0, margen: 30, precio_unitario: 0, descuento_pct: 0, subtotal: 0,
      moneda_item: "", tipo_cambio_item: "", proveedor_id: "", proveedor_nombre: "",
      producto_id: "", variante_id: "", variante_texto: "", imagen: null, imagen_comentario: ""
    }],
    observaciones: "",
    condiciones: "- Precios expresados en Guaraníes (IVA incluido).\n- Validez de la oferta: 15 días.\n- Forma de pago: Al contado.\n- Tiempo de entrega: A confirmar según stock.",
    descuento_global_tipo: "monto", // "monto" | "pct"
    descuento_global_monto: 0,
    descuento_global_pct: 0,
  });

  const [formData, setFormData] = useState(getDefaultFormData());
  const [editingId, setEditingId] = useState(null);
  // Copia local editable de `empresas` — permite reflejar al instante una entidad
  // recién creada desde el buscador, sin esperar a que el padre vuelva a hacer fetch.
  const [empresasLocal, setEmpresasLocal] = useState(empresas || []);
  useEffect(() => { setEmpresasLocal(empresas || []); }, [empresas]);
  const handleClienteCreado = (nuevo) => {
    setEmpresasLocal(prev => prev.some(e => String(e.id) === String(nuevo.id)) ? prev : [...prev, { ...nuevo, _soloLocal: true }]);
  };
  const [saving, setSaving] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [devolucionActiva, setDevolucionActiva] = useState(false);
  const [varianteScanPending, setVarianteScanPending] = useState(null); // { prod, variantesActivas, modo: "agregar"|"restar" }

  useEffect(() => {
    if (mode === "new" || mode === "create") {
      setFormData(getDefaultFormData());
      setEditingId(null);
    } else if (mode === "edit" && presupuesto) {
      const moneda = presupuesto.moneda || "PYG";
      const validez = presupuesto.validez_dias || 15;
      const formaPago = presupuesto.forma_pago || "contado";
      setEditingId(presupuesto.id);
      setFormData({
        empresa_id: presupuesto.empresa_id,
        numero: presupuesto.numero || "",
        nombre_archivo: presupuesto.nombre_archivo || "",
        logo_tipo: presupuesto.logo_tipo || "arandujar",
        moneda: moneda,
        forma_pago: formaPago,
        fecha: presupuesto.fecha,
        validez_dias: validez,
        tipo_cambio: presupuesto.tipo_cambio || "",
        modo: productosHabilitados ? (presupuesto.modo || "libre") : "libre",
        items: presupuesto.items.map(item => ({
          ...item, moneda_item: item.moneda_item || "", tipo_cambio_item: item.tipo_cambio_item || "",
          proveedor_id: item.proveedor_id || "", proveedor_nombre: item.proveedor_nombre || "",
          proveedores: (item.proveedores && item.proveedores.length) ? item.proveedores : (item.proveedor_id ? [{ id: item.proveedor_id, nombre: item.proveedor_nombre || "" }] : []),
          producto_id: item.producto_id || "", variante_id: item.variante_id || "", variante_texto: item.variante_texto || "", imagen: item.imagen || null,
          imagen_comentario: item.imagen_comentario || ""
        })),
        observaciones: presupuesto.observaciones || "",
        condiciones: buildCondiciones(moneda, validez, formaPago),
        descuento_global_tipo: (presupuesto.descuento_global_pct || 0) > 0 ? "pct" : "monto",
        descuento_global_monto: presupuesto.descuento_global_monto || 0,
        descuento_global_pct: presupuesto.descuento_global_pct || 0,
      });
    } else if (mode === "copy" && presupuesto) {
      const moneda = presupuesto.moneda || "PYG";
      const validez = presupuesto.validez_dias || 15;
      const formaPago = presupuesto.forma_pago || "contado";
      setEditingId(null);
      setFormData({
        empresa_id: presupuesto.empresa_id,
        numero: "",
        nombre_archivo: presupuesto.nombre_archivo || "",
        logo_tipo: presupuesto.logo_tipo || "arandujar",
        moneda: moneda,
        forma_pago: formaPago,
        fecha: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10),
        validez_dias: validez,
        tipo_cambio: presupuesto.tipo_cambio || "",
        modo: productosHabilitados ? (presupuesto.modo || "libre") : "libre",
        items: presupuesto.items.map(item => ({
          ...item, moneda_item: item.moneda_item || "", tipo_cambio_item: item.tipo_cambio_item || "",
          proveedor_id: item.proveedor_id || "", proveedor_nombre: item.proveedor_nombre || "",
          proveedores: (item.proveedores && item.proveedores.length) ? item.proveedores : (item.proveedor_id ? [{ id: item.proveedor_id, nombre: item.proveedor_nombre || "" }] : []),
          producto_id: item.producto_id || "", variante_id: item.variante_id || "", variante_texto: item.variante_texto || "", imagen: item.imagen || null,
          imagen_comentario: item.imagen_comentario || ""
        })),
        observaciones: presupuesto.observaciones || "",
        condiciones: buildCondiciones(moneda, validez, formaPago),
        descuento_global_tipo: (presupuesto.descuento_global_pct || 0) > 0 ? "pct" : "monto",
        descuento_global_monto: presupuesto.descuento_global_monto || 0,
        descuento_global_pct: presupuesto.descuento_global_pct || 0,
      });
    }
  }, [presupuesto, mode]); // eslint-disable-line

  const buildCondiciones = (moneda, validezDias = 15, condicionPago = "contado", diasCredito = 30, numCuotas = 3) => {
    const monedaText = moneda === "USD" ? "Dólares Americanos" : moneda === "BRL" ? "Reales Brasileños" : "Guaraníes";
    let pagoText = "Al contado";
    if (condicionPago === "credito_dias") pagoText = `A crédito a ${diasCredito} días`;
    else if (condicionPago === "credito_cuotas") pagoText = `En ${numCuotas} cuota${numCuotas > 1 ? "s" : ""} (referencial)`;
    return `- Precios expresados en ${monedaText} (IVA incluido).\n- Validez de la oferta: ${validezDias} días.\n- Forma de pago: ${pagoText}.\n- Tiempo de entrega: A confirmar según stock.`;
  };

  const updateMoneda = (newMoneda) => {
    setFormData(prev => ({
      ...prev,
      moneda: newMoneda,
      items: prev.items.map(item => calculateItemFn(item, newMoneda, prev.tipo_cambio)),
      condiciones: buildCondiciones(newMoneda, prev.validez_dias, prev.condicion_pago, prev.dias_credito, prev.num_cuotas_ref)
    }));
  };

  const updateTipoCambio = (newTC) => {
    setFormData(prev => ({
      ...prev,
      tipo_cambio: newTC,
      items: prev.items.map(item => calculateItemFn(item, prev.moneda, newTC))
    }));
  };

  const updateCondicionPago = (newCondicion) => {
    const isCredito = newCondicion !== "contado";
    setFormData(prev => ({
      ...prev,
      condicion_pago: newCondicion,
      forma_pago: isCredito ? "credito" : "contado",
      condiciones: buildCondiciones(prev.moneda, prev.validez_dias, newCondicion, prev.dias_credito, prev.num_cuotas_ref)
    }));
  };

  // keep for backward compat; now delegates to updateCondicionPago
  const updateFormaPago = (newFormaPago) => {
    const newCondicion = newFormaPago === "contado" ? "contado" : "credito_dias";
    setFormData(prev => ({
      ...prev,
      forma_pago: newFormaPago,
      condicion_pago: newCondicion,
      condiciones: buildCondiciones(prev.moneda, prev.validez_dias, newCondicion, prev.dias_credito, prev.num_cuotas_ref)
    }));
  };

  const roundByMoneda = (value, moneda) => {
    const m = moneda || formData.moneda;
    if (m === "USD" || m === "BRL") return Math.round(value * 100) / 100;
    return Math.round(value);
  };

  const calculateItemFn = (item, docMoneda, globalTC) => {
    const costo = parseFloat(item.costo) || 0;
    const margen = parseFloat(item.margen) || 0;
    const cantidad = parseFloat(item.cantidad) || 0;
    const monedaItem = item.moneda_item || docMoneda;

    let costoConvertido = costo;
    if (monedaItem !== docMoneda) {
      const tc = parseFloat(item.tipo_cambio_item) || parseFloat(globalTC) || 1;
      if (docMoneda === "PYG" && (monedaItem === "USD" || monedaItem === "BRL")) {
        costoConvertido = costo * tc;
      } else if ((docMoneda === "USD" || docMoneda === "BRL") && monedaItem === "PYG") {
        costoConvertido = tc > 0 ? costo / tc : 0;
      }
    }

    const round = (v) => roundByMoneda(v, docMoneda);
    const precio_unitario = round(costoConvertido * (1 + margen / 100));
    const descuento_pct = parseFloat(item.descuento_pct || 0) || 0;
    const subtotal = round(precio_unitario * cantidad * (1 - descuento_pct / 100));
    return { ...item, precio_unitario, subtotal };
  };

  const calculateItem = (item) => calculateItemFn(item, formData.moneda, formData.tipo_cambio);

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === "margen" || field === "costo" || field === "cantidad" || field === "descuento_pct") {
      // Permitir string vacío temporalmente para que el usuario pueda borrar y escribir sin trabas
      newItems[index] = { ...newItems[index], [field]: value };
    } else if (field === "proveedor_id") {
      // Varios proveedores por ítem: agrega a la lista (el primero queda en proveedor_id/nombre por compatibilidad)
      const prov = proveedores.find(p => p.id === value);
      const actuales = newItems[index].proveedores || [];
      if (prov && !actuales.some(x => x.id === prov.id)) {
        const lista = [...actuales, { id: prov.id, nombre: prov.nombre }];
        newItems[index] = { ...newItems[index], proveedores: lista, proveedor_id: lista[0].id, proveedor_nombre: lista[0].nombre };
      }
    } else if (field === "quitar_proveedor") {
      const lista = (newItems[index].proveedores || []).filter(x => x.id !== value);
      newItems[index] = { ...newItems[index], proveedores: lista, proveedor_id: lista[0]?.id || "", proveedor_nombre: lista[0]?.nombre || "" };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    newItems[index] = calculateItem(newItems[index]);
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        descripcion: "", observacion: "", observacion_oculta: "",
        cantidad: 1, costo: 0, margen: 30, precio_unitario: 0, descuento_pct: 0, subtotal: 0,
        moneda_item: "", tipo_cambio_item: "", proveedor_id: "", proveedor_nombre: "",
        producto_id: "", variante_id: "", variante_texto: "", imagen: null, imagen_comentario: ""
      }]
    });
  };

  const selectProductoForItem = (index, productoId, prodFromSelect = null, variante = null) => {
    const prod = productos.find(p => p.id === productoId && (!p.logo_tipo || p.logo_tipo === formData.logo_tipo));
    if (!prod) {
      const newItems = [...formData.items];
      newItems[index] = { ...newItems[index], producto_id: "", variante_id: "", variante_texto: "" };
      setFormData({ ...formData, items: newItems });
      return;
    }
    const newItems = [...formData.items];
    const selectedVariant = variante || prodFromSelect?.variantes?.find(v => v.id === newItems[index].variante_id) || null;
    const costo = selectedVariant?.precio_costo ?? prod.precio_costo ?? 0;
    const precioVenta = parseFloat(selectedVariant?.precio_venta ?? prod.precio_venta) || 0;
    // Calcular el margen que produce el precio de venta del catálogo
    const margenCatalogo = costo > 0 ? ((precioVenta / costo) - 1) * 100 : (newItems[index].margen || 30);
    const margen = margenCatalogo;
    // Si el producto está cargado en el catálogo en una moneda distinta a la del presupuesto,
    // heredamos esa moneda en el ítem — el mecanismo de conversión por ítem (tipo_cambio_item)
    // ya existente se encarga de convertir costo/precio a la moneda del documento.
    // Producto sin moneda cargada = Guaraníes por defecto (catálogo viejo).
    const monedaProdEfectiva = prod.moneda || "PYG";
    const monedaItem = monedaProdEfectiva !== formData.moneda ? monedaProdEfectiva : "";
    const calculated = calculateItemFn({ ...newItems[index], descripcion: prod.nombre, costo, margen, moneda_item: monedaItem, producto_id: productoId }, formData.moneda, formData.tipo_cambio);
    newItems[index] = {
      ...calculated,
      descripcion: prod.nombre,
      costo, margen,
      moneda_item: monedaItem,
      producto_id: productoId,
      variante_id: selectedVariant?.id || "",
      variante_texto: selectedVariant?.atributos_texto || "",
      precio_catalogo: precioVenta,
      margen_catalogo: margenCatalogo,
    };
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const roundDoc = (v) => roundByMoneda(v, formData.moneda);

  const calculateTotals = () => {
    const totalItems = formData.items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const totalDescuentosItems = formData.items.reduce((sum, item) => {
      const bruto = (parseFloat(item.precio_unitario) || 0) * (parseFloat(item.cantidad) || 0);
      return sum + Math.max(0, bruto - (parseFloat(item.subtotal) || 0));
    }, 0);

    const descuentoGlobalPct = Math.max(0, Math.min(100, parseFloat(formData.descuento_global_pct) || 0));
    const descuentoGlobalMontoIn = Math.max(0, parseFloat(formData.descuento_global_monto) || 0);
    const usaPct = formData.descuento_global_tipo === "pct";
    const descuentoGlobal = roundDoc(
      usaPct ? totalItems * (descuentoGlobalPct / 100) : Math.min(descuentoGlobalMontoIn, totalItems)
    );

    const total = Math.max(0, roundDoc(totalItems - descuentoGlobal));
    const total_descuentos = totalDescuentosItems + descuentoGlobal;
    const isDecimal = formData.moneda !== "PYG";
    const iva = isDecimal ? Math.round((total / 11) * 100) / 100 : Math.round(total / 11);
    const subtotal = isDecimal ? Math.round((total - iva) * 100) / 100 : total - iva;
    return { subtotal, iva, total, total_descuentos, descuento_global_monto: descuentoGlobal };
  };

  const formatNumber = (num, moneda = "PYG") => {
    if (moneda === "USD" || moneda === "BRL") {
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    }
    return new Intl.NumberFormat('es-PY').format(num);
  };

  const getCurrencySymbol = (moneda) => {
    if (moneda === "USD") return "US$";
    if (moneda === "BRL") return "R$";
    return "₲";
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleItemImageUpload = async (index, file) => {
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      const newItems = [...formData.items];
      newItems[index] = { ...newItems[index], imagen: base64 };
      setFormData({ ...formData, items: newItems });
    } catch (e) { console.error("Error al cargar imagen", e); }
  };

  const productosCatalogo = productos.filter(
    p => p.activo !== false && (!p.logo_tipo || p.logo_tipo === formData.logo_tipo)
  );

  const appendProductoPorBarcode = (prod, variante = null) => {
    setFormData(prev => {
      const items = [...(prev.items || [])];
      const idx = items.findIndex(item =>
        String(item.producto_id || "") === String(prod.id || "") &&
        String(item.variante_id || "") === String(variante?.id || "")
      );

      if (idx >= 0) {
        const cantidad = (parseFloat(items[idx].cantidad) || 0) + 1;
        items[idx] = calculateItemFn({ ...items[idx], cantidad }, prev.moneda, prev.tipo_cambio);
        return { ...prev, modo: productosHabilitados ? "productos" : prev.modo, items };
      }

      const precioVenta = parseFloat(variante?.precio_venta ?? prod.precio_venta ?? prod.precio ?? 0) || 0;
      const costo = parseFloat(variante?.precio_costo ?? prod.precio_costo ?? 0) || 0;
      const margenCatalogo = costo > 0 ? ((precioVenta / costo) - 1) * 100 : 0;
      // Heredar la moneda del producto si difiere de la del presupuesto — mismo mecanismo
      // de conversión por ítem que en selectProductoForItem. Sin moneda cargada = ₲ por defecto.
      const monedaProdEfectiva = prod.moneda || "PYG";
      const monedaItem = monedaProdEfectiva !== prev.moneda ? monedaProdEfectiva : "";
      const base = {
        descripcion: prod.nombre || "Producto",
        observacion: "",
        observacion_oculta: "",
        cantidad: 1,
        costo,
        margen: margenCatalogo,
        precio_unitario: precioVenta,
        precio_catalogo: precioVenta,
        margen_catalogo: margenCatalogo,
        descuento_pct: 0,
        subtotal: precioVenta,
        moneda_item: monedaItem,
        tipo_cambio_item: "",
        proveedor_id: "",
        proveedor_nombre: "",
        producto_id: prod.id || "",
        variante_id: variante?.id || "",
        variante_texto: variante?.atributos_texto || "",
        imagen: null,
        imagen_comentario: ""
      };
      const calculated = calculateItemFn(base, prev.moneda, prev.tipo_cambio);
      if (precioVenta > 0 && costo <= 0) {
        // Sin costo de referencia no hay margen para reconstruir el precio — convertir
        // el precio de venta directamente si la moneda del producto difiere de la del documento.
        const tc = parseFloat(base.tipo_cambio_item) || parseFloat(prev.tipo_cambio) || 1;
        let precioConvertido = precioVenta;
        if (monedaItem && monedaItem !== prev.moneda) {
          if (prev.moneda === "PYG" && (monedaItem === "USD" || monedaItem === "BRL")) precioConvertido = precioVenta * tc;
          else if ((prev.moneda === "USD" || prev.moneda === "BRL") && monedaItem === "PYG") precioConvertido = tc > 0 ? precioVenta / tc : precioVenta;
        }
        calculated.precio_unitario = precioConvertido;
        calculated.subtotal = precioConvertido;
      }

      const vacio = items.findIndex(item =>
        !item.descripcion &&
        !item.producto_id &&
        !(parseFloat(item.precio_unitario) > 0)
      );
      if (vacio >= 0) items[vacio] = calculated;
      else items.push(calculated);
      return { ...prev, modo: productosHabilitados ? "productos" : prev.modo, items };
    });
  };

  // Modo Devolución: cada escaneo resta 1 a la cantidad del ítem coincidente (por producto/variante,
  // o por descripción si el ítem fue cargado en modo libre sin producto_id). Si llega a 0, se quita la línea.
  const restarProductoPorBarcode = (prod, variante = null) => {
    let resultado = null; // "restado" | "eliminado" | "no_encontrado"
    setFormData(prev => {
      const items = [...(prev.items || [])];
      const idx = items.findIndex(item =>
        String(item.producto_id || "") === String(prod.id || "") &&
        String(item.variante_id || "") === String(variante?.id || "")
      );
      if (idx < 0) {
        resultado = "no_encontrado";
        return prev;
      }
      const cantidadActual = parseFloat(items[idx].cantidad) || 0;
      const cantidad = cantidadActual - 1;
      if (cantidad <= 0) {
        resultado = "eliminado";
        if (items.length === 1) {
          items[idx] = calculateItemFn({ ...items[idx], cantidad: 0, subtotal: 0 }, prev.moneda, prev.tipo_cambio);
          return { ...prev, items };
        }
        items.splice(idx, 1);
        return { ...prev, items };
      }
      resultado = "restado";
      items[idx] = calculateItemFn({ ...items[idx], cantidad }, prev.moneda, prev.tipo_cambio);
      return { ...prev, items };
    });
    return resultado;
  };

  const handleBarcodeSubmit = async () => {
    const code = (barcodeInput || "").trim();
    if (!code) return;

    let prod = productosCatalogo.find(p => (p.codigo_barras || "").trim() === code);
    if (!prod && API && token) {
      try {
        setBarcodeLoading(true);
        const q = new URLSearchParams();
        if (formData.logo_tipo) q.set("logo_tipo", formData.logo_tipo);
        const res = await fetch(`${API}/admin/productos/by-barcode/${encodeURIComponent(code)}?${q}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Producto no encontrado");
        }
        prod = await res.json();
      } catch (e) {
        toast.error(e.message || "No se encontró producto con ese código de barras");
        return;
      } finally {
        setBarcodeLoading(false);
      }
    }

    if (!prod) {
      toast.error("No se encontró producto con ese código de barras");
      return;
    }

    const variantesActivas = (prod.variantes || []).filter(v => v.activo !== false);
    const varianteMatch = prod.variantes_habilitadas && variantesActivas.length === 1 ? variantesActivas[0] : null;

    // Mismo código de barras con más de una variante activa (ej. mismo modelo,
    // distintos talles/colores): no podemos adivinar cuál es, mostramos un
    // selector rápido para que el usuario elija.
    if (prod.variantes_habilitadas && variantesActivas.length > 1) {
      setVarianteScanPending({
        prod,
        variantesActivas,
        modo: devolucionActiva ? "restar" : "agregar",
      });
      setBarcodeInput("");
      return;
    }

    if (devolucionActiva) {
      const resultado = restarProductoPorBarcode(prod, varianteMatch);
      if (resultado === "no_encontrado") {
        toast.error(`${prod.nombre} no está cargado en este presupuesto`);
      } else if (resultado === "eliminado") {
        toast.success(`${prod.nombre} quitado del presupuesto`);
      } else {
        toast.success(`${prod.nombre}: −1 unidad`);
      }
    } else {
      appendProductoPorBarcode(prod, varianteMatch);
      toast.success(`${prod.nombre} agregado al presupuesto`);
    }
    setBarcodeInput("");
  };

  // Confirma la variante elegida en el selector rápido de escaneo.
  const handleVarianteScanSelect = (variante) => {
    if (!varianteScanPending) return;
    const { prod, modo } = varianteScanPending;
    const etiqueta = variante.atributos_texto || "variante";
    if (modo === "restar") {
      const resultado = restarProductoPorBarcode(prod, variante);
      if (resultado === "no_encontrado") {
        toast.error(`${prod.nombre} (${etiqueta}) no está cargado en este presupuesto`);
      } else if (resultado === "eliminado") {
        toast.success(`${prod.nombre} (${etiqueta}) quitado del presupuesto`);
      } else {
        toast.success(`${prod.nombre} (${etiqueta}): −1 unidad`);
      }
    } else {
      appendProductoPorBarcode(prod, variante);
      toast.success(`${prod.nombre} (${etiqueta}) agregado al presupuesto`);
    }
    setVarianteScanPending(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.empresa_id) {
      toast.error("Seleccione una empresa");
      return;
    }

    const totals = calculateTotals();
    const faltaVariante = formData.modo === "productos" && formData.items.some(item => {
      if (!item.producto_id) return false;
      const prod = productos.find(p => String(p.id) === String(item.producto_id));
      return prod?.variantes_habilitadas && !item.variante_id;
    });
    if (faltaVariante) {
      toast.error("Hay productos con variantes sin variante seleccionada");
      return;
    }
    setSaving(true);

    try {
      const url = editingId
        ? `${API}/admin/presupuestos/${editingId}`
        : `${API}/admin/presupuestos`;

      const toFloat = v => (v === "" || v === null || v === undefined) ? null : parseFloat(v) || null;
      const payload = {
        empresa_id: formData.empresa_id,
        logo_tipo: activeEmpresaPropia?.slug || formData.logo_tipo,
        moneda: formData.moneda,
        forma_pago: formData.forma_pago,
        condicion_pago: formData.condicion_pago || "contado",
        dias_credito: parseInt(formData.dias_credito) || 30,
        num_cuotas_ref: formData.condicion_pago === "credito_cuotas" ? (parseInt(formData.num_cuotas_ref) || 3) : null,
        tasa_interes_ref: formData.condicion_pago === "credito_cuotas" ? (Number(formData.tasa_interes_ref) || 0) : null,
        numero: formData.numero || null,
        nombre_archivo: formData.nombre_archivo || null,
        fecha: formData.fecha,
        validez_dias: parseInt(formData.validez_dias, 10) || 15,
        tipo_cambio: toFloat(formData.tipo_cambio),
        modo: formData.modo || "libre",
        observaciones: formData.observaciones || null,
        condiciones: formData.condiciones || null,
        ...totals,
        descuento_global_monto: formData.descuento_global_tipo === "monto" ? (parseFloat(formData.descuento_global_monto) || 0) : 0,
        descuento_global_pct: formData.descuento_global_tipo === "pct" ? (parseFloat(formData.descuento_global_pct) || 0) : 0,
        items: formData.items.map(item => ({
          descripcion: (item.descripcion || "").trim() || "Sin descripción",
          cantidad: Math.max(1, parseInt(item.cantidad, 10) || 1),
          costo: parseFloat(item.costo) || 0,
          margen: parseFloat(item.margen) || 0,
          precio_unitario: parseFloat(item.precio_unitario) || 0,
          descuento_pct: Math.min(100, Math.max(0, parseFloat(item.descuento_pct || 0) || 0)),
          subtotal: parseFloat(item.subtotal) || 0,
          producto_id: item.producto_id || null,
          variante_id: item.variante_id || null,
          variante_texto: item.variante_texto || null,
          observacion: item.observacion || null,
          observacion_oculta: item.observacion_oculta || null,
          imagen: item.imagen || null,
          imagen_comentario: item.imagen_comentario || null,
          moneda_item: item.moneda_item || null,
          tipo_cambio_item: toFloat(item.tipo_cambio_item),
          proveedor_id: (item.proveedores?.[0]?.id) || item.proveedor_id || null,
          proveedor_nombre: (item.proveedores?.[0]?.nombre) || item.proveedor_nombre || null,
          proveedores: (item.proveedores && item.proveedores.length) ? item.proveedores : null
        }))
      };

      const response = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const savedPresupuesto = await response.json();
        toast.success(editingId ? "Presupuesto actualizado" : "Presupuesto creado");
        onSaved(savedPresupuesto);
      } else {
        let detail = "Error al guardar";
        try {
          const err = await response.json();
          detail = err.detail
            ? (Array.isArray(err.detail) ? err.detail.map(d => d.msg || d).join("; ") : String(err.detail))
            : detail;
        } catch (_) { /* ignore */ }
        toast.error(detail);
      }
    } catch (error) {
      toast.error("Error al guardar el presupuesto");
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();

  return (
    <>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-arandu-dark-light border border-white/10 rounded-2xl p-6 w-full max-w-[96vw] xl:max-w-7xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            <Calculator className="w-6 h-6 text-arandu-blue" />
            {editingId ? "Editar Presupuesto" : "Nuevo Presupuesto"}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Empresa & Logo Selection */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Empresa *</label>
              <ClienteSearchSelect
                empresas={(defaultEmpresaId ? empresasLocal.filter(emp => emp.id === defaultEmpresaId) : empresasLocal)
                  .filter(emp => !formData.logo_tipo || !emp.logo_tipo || emp.logo_tipo === formData.logo_tipo)}
                value={(() => {
                  const emp = empresasLocal.find(em => String(em.id) === String(formData.empresa_id));
                  return emp ? { id: emp.id, nombre: emp.nombre, razon_social: emp.razon_social, ruc: emp.ruc } : null;
                })()}
                onSelect={(emp) => {
                  if (!emp?.id) {
                    setFormData(prev => ({ ...prev, empresa_id: "" }));
                    return;
                  }
                  setFormData(prev => ({
                    ...prev,
                    empresa_id: emp.id,
                    logo_tipo: activeEmpresaPropia?.slug || emp?.logo_tipo || prev.logo_tipo,
                  }));
                }}
                onCreated={handleClienteCreado}
                token={token}
                logo={getDefaultLogo()}
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Fecha del Presupuesto</label>
              <DateInput
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                className="bg-arandu-dark border-white/10 text-white"
              />
            </div>
          </div>

          {/* Número de Presupuesto */}
          <div>
            <label className="block text-slate-400 text-sm mb-2">
              Número de Presupuesto
              <span className="ml-2 text-slate-500 text-xs">(dejar en blanco para asignar automáticamente)</span>
            </label>
            <Input
              type="text"
              value={formData.numero}
              onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
              placeholder={editingId ? formData.numero || "Ej: P2025-0042" : "Ej: P2025-0042"}
              className="bg-arandu-dark border-white/10 text-white placeholder:text-slate-600"
            />
          </div>

          {/* Nombre del archivo */}
          <div>
            <label className="block text-slate-400 text-sm mb-2">
              Nombre del presupuesto
              <span className="ml-2 text-slate-500 text-xs">(descripción corta para identificarlo fácilmente)</span>
            </label>
            <Input
              type="text"
              value={formData.nombre_archivo}
              onChange={(e) => setFormData({ ...formData, nombre_archivo: e.target.value })}
              placeholder="Ej: Renovación servidores, Soporte anual cliente..."
              className="bg-arandu-dark border-white/10 text-white placeholder:text-slate-600"
            />
          </div>

          {/* Empresa activa */}
          {activeEmpresaPropia && (
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-white/10 bg-white/3">
              {activeEmpresaPropia.logo_url ? (
                <img src={activeEmpresaPropia.logo_url} alt={activeEmpresaPropia.nombre} className="h-6 object-contain" />
              ) : (
                <span className="w-4 h-4 rounded-full" style={{ backgroundColor: activeEmpresaPropia.color || "#3b82f6" }} />
              )}
              <span className="text-white text-sm font-medium">{activeEmpresaPropia.nombre}</span>
              <span className="text-slate-500 text-xs ml-auto">Empresa activa</span>
            </div>
          )}

          {/* Moneda Selection */}
          <div>
            <label className="block text-slate-400 text-sm mb-2">Moneda</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => updateMoneda("PYG")}
                className={`flex-1 p-3 rounded-lg border transition-all ${
                  formData.moneda === "PYG"
                    ? 'border-green-500 bg-green-500/20'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <span className={`text-sm font-medium ${formData.moneda === "PYG" ? 'text-green-400' : 'text-slate-400'}`}>
                  ₲ Guaraníes
                </span>
              </button>
              <button
                type="button"
                onClick={() => updateMoneda("USD")}
                className={`flex-1 p-3 rounded-lg border transition-all ${
                  formData.moneda === "USD"
                    ? 'border-green-500 bg-green-500/20'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <span className={`text-sm font-medium ${formData.moneda === "USD" ? 'text-green-400' : 'text-slate-400'}`}>
                  US$ Dólares
                </span>
              </button>
              {activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") && (
                <button
                  type="button"
                  onClick={() => updateMoneda("BRL")}
                  className={`px-3 py-1.5 rounded-lg border transition-all ${
                    formData.moneda === "BRL"
                      ? 'border-green-500 bg-green-500/20'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <span className={`text-sm font-medium ${formData.moneda === "BRL" ? 'text-green-400' : 'text-slate-400'}`}>
                    R$
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Tipo de Cambio Global */}
          <div>
            <label className="block text-slate-400 text-sm mb-2">
              Tipo de cambio
              <span className="ml-2 text-slate-500 text-xs">
                {formData.moneda === "PYG" ? "USD 1 = ₲ ?" : "₲ ? = USD 1"} — usado cuando un ítem tiene moneda diferente al documento
              </span>
            </label>
            <Input
              type="number"
              value={formData.tipo_cambio}
              onChange={(e) => updateTipoCambio(e.target.value)}
              placeholder={formData.moneda === "PYG" ? "Ej: 7800" : "Ej: 7800"}
              className="bg-arandu-dark border-white/10 text-white"
            />
          </div>

          {/* Condición de Pago */}
          <div>
            <label className="block text-slate-400 text-sm mb-2">Condición de Pago</label>
            <div className="flex gap-2">
              {[
                { value: "contado",       label: "💵 Contado" },
                { value: "credito_dias",  label: "📅 Crédito días" },
                { value: "credito_cuotas", label: "📋 Cuotas" },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateCondicionPago(opt.value)}
                  className={`flex-1 p-2.5 rounded-lg border text-sm font-medium transition-all ${
                    formData.condicion_pago === opt.value
                      ? 'border-arandu-blue bg-arandu-blue/20 text-arandu-blue'
                      : 'border-white/10 hover:border-white/30 text-slate-400'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Crédito días: selector 15/30/60 */}
            {formData.condicion_pago === "credito_dias" && (
              <div className="mt-3 flex gap-2">
                {[15, 30, 60].map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      dias_credito: d,
                      condiciones: buildCondiciones(prev.moneda, prev.validez_dias, "credito_dias", d, prev.num_cuotas_ref)
                    }))}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                      formData.dias_credito === d
                        ? 'border-cyan-500 bg-cyan-500/15 text-cyan-400'
                        : 'border-white/10 hover:border-white/30 text-slate-400'
                    }`}
                  >
                    {d} días
                  </button>
                ))}
              </div>
            )}

            {/* Crédito cuotas */}
            {formData.condicion_pago === "credito_cuotas" && (
              <div className="mt-3 space-y-3 p-3 rounded-lg border border-violet-500/20 bg-violet-500/5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">N° cuotas (ref.)</label>
                    <select
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      value={formData.num_cuotas_ref}
                      onChange={e => {
                        const n = Number(e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          num_cuotas_ref: n,
                          condiciones: buildCondiciones(prev.moneda, prev.validez_dias, "credito_cuotas", prev.dias_credito, n)
                        }));
                      }}
                    >
                      {[1,2,3,4,5,6,8,10,12,18,24].map(n => (
                        <option key={n} value={n}>{n} cuota{n>1?"s":""}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Interés % (ref.)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={formData.tasa_interes_ref}
                      onChange={e => setFormData(prev => ({ ...prev, tasa_interes_ref: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-500">
                  Referencial — los valores reales se configuran al facturar.
                </p>
              </div>
            )}
          </div>

          {/* Mode Toggle */}
          {productosHabilitados ? (
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <span className="text-slate-400 text-sm">Modo de carga:</span>
              <div className="flex gap-2 flex-wrap">
                {canModoLibre && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, modo: "libre" }))}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      formData.modo !== "productos"
                        ? 'border-arandu-blue bg-arandu-blue/20 text-arandu-blue'
                        : 'border-white/10 text-slate-400 hover:border-white/30'
                    }`}
                  >
                    ✏️ Libre
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, modo: "productos" }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                    formData.modo === "productos"
                      ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                      : 'border-white/10 text-slate-400 hover:border-white/30'
                  }`}
                >
                  📦 Por catálogo
                </button>
                <button
                  type="button"
                  onClick={() => setDevolucionActiva(prev => !prev)}
                  title="Al activarlo, cada escaneo resta 1 a la cantidad del ítem en vez de sumar"
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                    devolucionActiva
                      ? 'border-red-500 bg-red-500/20 text-red-400'
                      : 'border-white/10 text-slate-400 hover:border-white/30'
                  }`}
                >
                  🔁 Devolución
                </button>
              </div>
              {devolucionActiva && (
                <span className="text-[11px] text-red-300">Modo activo: al escanear un código, resta 1 unidad del ítem correspondiente.</span>
              )}
            </div>
          ) : null}

          {/* Items Table */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <label className="block text-slate-400 text-sm">Productos / Servicios</label>
              {formData.modo !== "productos" && productosHabilitados && (
                <span className="text-[11px] text-slate-500">Al escanear se activa catálogo automáticamente.</span>
              )}
            </div>

            {productosHabilitados && (
              <div className={`mb-3 flex flex-wrap items-end gap-2 p-3 rounded-xl border ${devolucionActiva ? 'border-red-500/30 bg-red-500/10' : 'border-emerald-500/20 bg-emerald-500/10'}`}>
                <div className="flex-1 min-w-[220px]">
                  <label className={`block text-xs font-medium mb-1 ${devolucionActiva ? 'text-red-200' : 'text-emerald-200'}`}>
                    Código de barras {devolucionActiva && "(modo Devolución)"}
                  </label>
                  <Input
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleBarcodeSubmit(); } }}
                    placeholder="Escanear o escribir y Enter"
                    disabled={barcodeLoading}
                    className={`bg-arandu-dark text-white placeholder:text-slate-500 ${devolucionActiva ? 'border-red-500/30' : 'border-emerald-500/20'}`}
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleBarcodeSubmit}
                  disabled={barcodeLoading || !barcodeInput.trim()}
                  className={devolucionActiva ? "bg-red-600 hover:bg-red-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                >
                  {barcodeLoading ? "Buscando..." : (devolucionActiva ? "Restar" : "Agregar")}
                </Button>
                <p className={`w-full text-[11px] ${devolucionActiva ? 'text-red-200/80' : 'text-emerald-200/80'}`}>
                  {devolucionActiva
                    ? "Cada escaneo resta 1 unidad del ítem coincidente. Si llega a 0, se quita del presupuesto."
                    : "Si el producto ya está en el presupuesto, se suma 1 a la cantidad."}
                </p>
              </div>
            )}

            <div className="bg-arandu-dark rounded-lg overflow-x-auto">
              <table className="w-full min-w-[1180px] text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3 text-slate-400 font-medium">Descripción</th>
                    <th className="text-center p-3 text-slate-400 font-medium w-20">Cant.</th>
                    {(formData.modo !== "productos" || puedeVerCostoCompra) && (
                      <th className="text-right p-3 text-slate-400 font-medium w-36">
                        Costo
                        <span className="block text-xs text-slate-400 font-normal">moneda</span>
                      </th>
                    )}
                    {(formData.modo !== "productos" || puedeVerCostoCompra) && (
                      <th className="text-center p-3 text-slate-400 font-medium w-20">% Gan.</th>
                    )}
                    <th className="text-right p-3 text-slate-400 font-medium w-32">
                      Precio Unit.
                      <span className="block text-xs text-slate-400 font-normal">{getCurrencySymbol(formData.moneda)}</span>
                    </th>
                    {canUseDescuento && (
                      <th className="text-right p-3 text-slate-400 font-medium w-20">Desc.%</th>
                    )}
                    <th className="text-right p-3 text-slate-400 font-medium w-32">
                      Subtotal
                      <span className="block text-xs text-slate-400 font-normal">{getCurrencySymbol(formData.moneda)}</span>
                    </th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index} className="border-b border-white/5">
                      <td className="p-2 min-w-0">
                        {formData.modo === "productos" && (
                          <div className="mb-1 min-w-0">
                            <ProductoSearchSelect
                              productos={productosCatalogo}
                              value={item.producto_id || ""}
                              variantValue={item.variante_id || ""}
                              includeVariants={true}
                              onChange={(pid, prod, variante) => selectProductoForItem(index, pid, prod, variante)}
                              placeholder="Buscar producto del catálogo..."
                            />
                          </div>
                        )}
                        <Input
                          type="text"
                          value={item.descripcion}
                          onChange={(e) => updateItem(index, "descripcion", e.target.value)}
                          className="bg-transparent border-white/10 text-white text-sm mb-1"
                          placeholder="Descripción del producto..."
                        />
                        {item.variante_texto && (
                          <div className="inline-flex max-w-full items-center rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 text-[10px] text-emerald-300 mb-1">
                            <span className="truncate">Variante: {item.variante_texto}</span>
                          </div>
                        )}
                        <Input
                          type="text"
                          value={item.observacion || ""}
                          onChange={(e) => updateItem(index, "observacion", e.target.value)}
                          className="bg-transparent border-white/5 text-yellow-400/80 text-xs italic"
                          placeholder="Obs: destino, ubicación..."
                        />
                        <Input
                          type="text"
                          value={item.observacion_oculta || ""}
                          onChange={(e) => updateItem(index, "observacion_oculta", e.target.value)}
                          className="bg-transparent border-dashed border-white/5 text-orange-300/80 text-xs italic mt-1"
                          placeholder="🔒 Obs interna (solo visible en costos)"
                          title="Esta nota solo se ve en la vista de costos, no aparece en el presupuesto final"
                        />
                        {proveedores.filter(p => !p.logo_tipo || p.logo_tipo === formData.logo_tipo).length > 0 && (
                          <>
                          {(item.proveedores || []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {item.proveedores.map(pv => (
                                <span key={pv.id} className="inline-flex items-center gap-1 text-[10px] bg-blue-500/15 text-blue-300 border border-blue-500/30 px-1.5 py-0.5 rounded-full">
                                  {pv.nombre}
                                  <button type="button" onClick={() => updateItem(index, "quitar_proveedor", pv.id)} className="hover:text-red-300" title="Quitar proveedor">×</button>
                                </span>
                              ))}
                            </div>
                          )}
                          <select
                            value=""
                            onChange={e => updateItem(index, "proveedor_id", e.target.value)}
                            className="mt-1 w-full bg-transparent border-0 border-b border-white/10 text-slate-500 text-xs font-body focus:outline-none focus:border-arandu-blue hover:text-slate-300 transition-colors"
                          >
                            <option value="">{(item.proveedores || []).length ? "+ Agregar otro proveedor" : "— Proveedor (opcional) —"}</option>
                            {proveedores
                              .filter(p => !p.logo_tipo || p.logo_tipo === formData.logo_tipo)
                              .map(p => (
                                <option key={p.id} value={p.id}>{p.nombre}</option>
                              ))}
                          </select>
                          </>
                        )}
                        {/* Imagen del ítem */}
                        <div className="mt-2">
                          {item.imagen ? (
                            <div className="border border-white/10 rounded-lg overflow-hidden">
                              <img src={item.imagen} alt="" className="w-full max-h-24 object-contain bg-black/20" />
                              <div className="flex gap-1 p-1">
                                <input
                                  type="text"
                                  value={item.imagen_comentario || ""}
                                  onChange={e => updateItem(index, "imagen_comentario", e.target.value)}
                                  className="flex-1 bg-transparent text-yellow-300/80 text-xs px-1 py-0.5 border-0 border-b border-white/10 focus:outline-none placeholder-slate-600"
                                  placeholder="Comentario de imagen..."
                                />
                                <button
                                  type="button"
                                  onClick={() => { updateItem(index, "imagen", null); updateItem(index, "imagen_comentario", ""); }}
                                  className="text-red-400 hover:text-red-300 text-xs px-1"
                                  title="Quitar imagen"
                                >✕</button>
                              </div>
                            </div>
                          ) : (
                            <label className="cursor-pointer flex items-center gap-1 text-slate-600 hover:text-slate-400 text-xs transition-colors">
                              <span>📷</span>
                              <span>Agregar imagen</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={e => handleItemImageUpload(index, e.target.files[0])}
                              />
                            </label>
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={item.cantidad}
                          onChange={(e) => updateItem(index, "cantidad", e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="bg-transparent border-white/10 text-white text-sm text-center"
                          min="0"
                          step="any"
                        />
                      </td>
                      {(formData.modo !== "productos" || puedeVerCostoCompra) && (
                        <td className="p-2">
                          {/* Selector de moneda del ítem */}
                          <div className="flex gap-1 mb-1 justify-end">
                            {["PYG", "USD"].map(m => {
                              const isActive = (item.moneda_item || formData.moneda) === m;
                              const isDocMoneda = m === formData.moneda;
                              return (
                                <button
                                  key={m}
                                  type="button"
                                  onClick={() => updateItem(index, "moneda_item", isDocMoneda ? "" : m)}
                                  className={`px-1.5 py-0.5 rounded text-xs font-semibold transition-all border ${
                                    isActive
                                      ? m === "PYG"
                                        ? "bg-green-500/25 text-green-300 border-green-500/50"
                                        : "bg-blue-500/25 text-blue-300 border-blue-500/50"
                                      : "bg-white/5 text-slate-500 border-white/10 hover:border-white/30"
                                  }`}
                                >
                                  {m === "PYG" ? "₲" : "$"}
                                </button>
                              );
                            })}
                          </div>
                          <MoneyInput
                            value={item.costo}
                            moneda={item.moneda_item || formData.moneda}
                            onChange={(value) => updateItem(index, "costo", value)}
                            onFocus={(e) => e.target.select()}
                            className="bg-transparent border-white/10 text-white text-sm text-right"
                            placeholder="0"
                          />
                          {/* Override de TC por ítem, solo si la moneda difiere */}
                          {(item.moneda_item && item.moneda_item !== formData.moneda) && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-slate-500 whitespace-nowrap">TC:</span>
                              <Input
                                type="number"
                                value={item.tipo_cambio_item || ""}
                                onChange={(e) => updateItem(index, "tipo_cambio_item", e.target.value)}
                                className="bg-transparent border-white/5 text-slate-400 text-xs text-right"
                                placeholder={formData.tipo_cambio ? String(formData.tipo_cambio) : "global"}
                              />
                            </div>
                          )}
                        </td>
                      )}
                      {(formData.modo !== "productos" || puedeVerCostoCompra) && (
                        <td className="p-2">
                          {isCatalogItem(item) ? (
                            <span className="block text-center text-slate-600 text-xs">—</span>
                          ) : (
                            <Input
                              type="number"
                              value={item.margen}
                              onChange={(e) => updateItem(index, "margen", e.target.value)}
                              onFocus={(e) => e.target.select()}
                              className="bg-transparent border-white/10 text-arandu-blue text-sm text-center font-medium"
                              placeholder="30"
                            />
                          )}
                        </td>
                      )}
                      <td className="p-2 text-right">
                        {isCatalogItem(item) ? (
                          <div>
                            <MoneyInput
                              className={`w-full bg-transparent border border-white/10 rounded px-2 py-1 text-sm text-right text-slate-300 focus:outline-none focus:border-arandu-blue ${isPrecioLocked(item) ? "opacity-60 cursor-not-allowed" : ""}`}
                              value={item.precio_unitario}
                              moneda={formData.moneda}
                              readOnly={isPrecioLocked(item)}
                              onChange={(value) => handlePrecioDirectChange(index, value)}
                              onBlur={() => {
                                // snap back if out of range
                                const catalogo = parseFloat(item.precio_catalogo ?? item.precio_unitario) || 0;
                                const val = parseFloat(item.precio_unitario) || 0;
                                if (!canModifyPrecioAbajo && val < catalogo) handlePrecioDirectChange(index, catalogo);
                                if (!canModifyPrecioArriba && val > catalogo) handlePrecioDirectChange(index, catalogo);
                              }}
                              title={isPrecioLocked(item) ? "Sin permiso para modificar el precio del catálogo" : undefined}
                            />
                            {item.precio_catalogo != null && parseFloat(item.precio_unitario) !== parseFloat(item.precio_catalogo) && (
                              <span className={`text-[9px] font-medium block text-right mt-0.5 ${parseFloat(item.precio_unitario) < parseFloat(item.precio_catalogo) ? "text-red-400" : "text-emerald-400"}`}>
                                {parseFloat(item.precio_unitario) < parseFloat(item.precio_catalogo) ? "▼ con descuento" : "▲ por encima"}
                              </span>
                            )}
                            {isPrecioLocked(item) && (
                              <span className="text-[9px] text-slate-400 block text-right mt-0.5">🔒 precio fijo</span>
                            )}
                            <span className="text-[10px] text-slate-500 block text-right">{getCurrencySymbol(formData.moneda)}</span>
                          </div>
                        ) : (
                          <span className="text-slate-300">
                            {formatNumber(item.precio_unitario, formData.moneda)} {getCurrencySymbol(formData.moneda)}
                          </span>
                        )}
                      </td>
                      {canUseDescuento && (
                        <td className="p-2 text-right">
                          <input
                            type="number"
                            className="w-full bg-transparent border border-white/10 rounded px-2 py-1 text-sm text-right text-slate-300 focus:outline-none focus:border-arandu-blue"
                            value={item.descuento_pct ?? 0}
                            onChange={(e) => updateItem(index, "descuento_pct", e.target.value)}
                            min="0"
                            max="100"
                            step="0.5"
                            title="Descuento %"
                            placeholder="0"
                          />
                          {(parseFloat(item.descuento_pct) > 0) && (
                            <span className="text-[9px] text-emerald-400 block text-right mt-0.5">−{parseFloat(item.descuento_pct)}%</span>
                          )}
                        </td>
                      )}
                      <td className="p-2 text-right text-white font-medium">
                        {formatNumber(item.subtotal, formData.moneda)} {getCurrencySymbol(formData.moneda)}
                      </td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-slate-600 hover:text-red-400 transition-colors"
                          title="Eliminar item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Add Item Button */}
              <div className="border-t border-white/10 p-3">
                <Button
                  type="button"
                  onClick={addItem}
                  variant="ghost"
                  className="text-arandu-blue hover:bg-arandu-blue/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Item
                </Button>
              </div>

              {/* Descuento Global */}
              {puedeDescuentoGlobal && (
                <div className="mt-4 flex flex-wrap items-end gap-2 p-3 rounded-xl border border-arandu-blue/20 bg-arandu-blue/5">
                  <div className="w-36">
                    <label className="block text-slate-400 text-xs mb-1">Descuento global</label>
                    <select
                      value={formData.descuento_global_tipo}
                      onChange={(e) => setFormData(prev => ({ ...prev, descuento_global_tipo: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 text-white rounded-md px-2 py-2 text-sm"
                    >
                      <option value="monto">Monto ({getCurrencySymbol(formData.moneda)})</option>
                      <option value="pct">Porcentaje (%)</option>
                    </select>
                  </div>
                  <div className="w-36">
                    {formData.descuento_global_tipo === "pct" ? (
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={formData.descuento_global_pct}
                        onChange={(e) => setFormData(prev => ({ ...prev, descuento_global_pct: e.target.value }))}
                        placeholder="Ej: 10"
                        className="bg-arandu-dark border-white/10 text-white text-sm"
                      />
                    ) : (
                      <MoneyInput
                        value={formData.descuento_global_monto}
                        moneda={formData.moneda}
                        onChange={(value) => setFormData(prev => ({ ...prev, descuento_global_monto: value }))}
                        className="bg-arandu-dark border-white/10 text-white text-sm"
                        placeholder="0"
                      />
                    )}
                  </div>
                  {totals.descuento_global_monto > 0 && (
                    <span className="text-[11px] text-emerald-400 pb-2">
                      −{formatNumber(totals.descuento_global_monto, formData.moneda)} {getCurrencySymbol(formData.moneda)} aplicado sobre el total
                    </span>
                  )}
                </div>
              )}

              {/* Totals */}
              <div className="mt-4 flex justify-end p-3">
                <div className="bg-arandu-dark rounded-lg p-4 w-72">
                  <div className="flex justify-between text-white font-bold text-lg mb-2 pb-2 border-b border-white/10">
                    <span>TOTAL:</span>
                    <span className="text-arandu-blue">{formatNumber(totals.total, formData.moneda)} {getCurrencySymbol(formData.moneda)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400 text-sm mb-1">
                    <span>Total ítems:</span>
                    <span>{formData.items.reduce((s, it) => s + (parseFloat(it.cantidad) || 0), 0)}</span>
                  </div>
                  {totals.total_descuentos > 0 && (
                    <div className="flex justify-between text-emerald-400 text-sm mb-1">
                      <span>Descuentos aplicados:</span>
                      <span>-{formatNumber(totals.total_descuentos, formData.moneda)} {getCurrencySymbol(formData.moneda)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-500 text-sm">
                    <span>Base imponible:</span>
                    <span>{formatNumber(totals.subtotal, formData.moneda)} {getCurrencySymbol(formData.moneda)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500 text-sm">
                    <span>IVA incluido (10%):</span>
                    <span>{formatNumber(totals.iva, formData.moneda)} {getCurrencySymbol(formData.moneda)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Observaciones */}
            <div>
              <label className="block text-slate-400 text-sm mb-2">Observaciones</label>
              <Textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                className="bg-arandu-dark border-white/10 text-white resize-none"
                rows={2}
                placeholder="Observaciones adicionales..."
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-white/20 text-slate-300"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-arandu-blue hover:bg-arandu-blue-dark text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Guardando..." : (editingId ? "Actualizar Presupuesto" : "Crear Presupuesto")}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>

    {/* Selector rápido de variante al escanear un código de barras compartido
        por más de una variante activa (mismo talle/color agrupados bajo un
        único código de barras a nivel de producto). */}
    {varianteScanPending && (
      <div
        className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      >
        <div
          className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Elegí la variante</h3>
              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">
                {varianteScanPending.prod.nombre}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setVarianteScanPending(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto p-2">
            {varianteScanPending.variantesActivas.map((v) => {
              const stock = v.stock_actual ?? 0;
              const stockBajo = stock <= 0;
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={varianteScanPending.modo === "agregar" && stockBajo}
                  onClick={() => handleVarianteScanSelect(v)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-left transition-colors mb-1 last:mb-0 ${
                    varianteScanPending.modo === "agregar" && stockBajo
                      ? "opacity-50 cursor-not-allowed bg-gray-50"
                      : "hover:bg-emerald-50 border border-transparent hover:border-emerald-200"
                  }`}
                >
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {v.atributos_texto || "Variante"}
                  </span>
                  <span className={`text-xs font-semibold whitespace-nowrap ${stockBajo ? "text-red-500" : "text-emerald-600"}`}>
                    Stock: {stock}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <p className="text-[11px] text-gray-400">
              {varianteScanPending.modo === "restar"
                ? "Se restará 1 unidad de la variante elegida."
                : "Se agregará 1 unidad de la variante elegida."}
            </p>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default PresupuestoFormModal;
